import { Address, Firm } from '@eo-storefronts/eo-core'
import AddressFormAction from '~/src/components/customers/addresses/AddressFormAction'
import AddressFormFields from '~/src/components/customers/addresses/AddressFormFields'
import { FormikProps, useFormik } from 'formik'
import { useEoValue } from '~/src/hooks/useEoState'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import { AddressFormInterface } from '~/src/types/forms/AddressFormInterface'
import * as yup from 'yup'
import { useTranslations } from '~/src/hooks/useTranslations'
import useAddressForm from '~/src/hooks/customer/useAddressForm'

interface Props {
  address?: Address | null,
  showSetAsDefault?: boolean,
  redirectAfterSubmit?: boolean,
  showDescription?: boolean,
  closeAfterSubmit?: boolean,
  onSubmit?(address: Address): void,
}

const AddressForm = ({
  address,
  showSetAsDefault = true,
  showDescription = true,
  redirectAfterSubmit = true,
  closeAfterSubmit = false,
  onSubmit
}: Props) => {
  const { t } = useTranslations()
  const firm = useEoValue<Firm | null>(FIRM_SELECTOR)
  const { submit } = useAddressForm()
  const formik: FormikProps<AddressFormInterface> = useFormik<AddressFormInterface>({
    initialValues: {
      id: address?.id ?? 0,
      name: address?.description ?? '',
      isDefault: address?.is_default ?? false,
      street: address?.street ?? '',
      houseNumber: address?.house_number ?? '',
      bus: address?.bus ?? '',
      locality: address?.locality ?? '',
      zipCode: address?.zip_code ?? '',
      country: btoa(encodeURIComponent(address?.country ? JSON.stringify(address.country) : JSON.stringify(firm?.address.country)))
    },
    validationSchema: yup.object({
      name: showDescription ? yup.string().required(t('errors.address_name_required')) : yup.string(),
      isDefault: showSetAsDefault ? yup.boolean().required() : yup.boolean(),
      street: yup.string().required(t('errors.street_name_required')),
      houseNumber: yup.string().required(t('errors.house_number_required')),
      bus: yup.string(),
      locality: yup.string().required(t('errors.locality_required')),
      zipCode: yup.string().required(t('errors.zipcode_required')),
      country: yup.string().required(t('errors.country_required'))
    }),
    onSubmit: ({ id, name, isDefault, street, houseNumber, bus, locality, zipCode, country }: AddressFormInterface) => {
      void submit({
        id,
        description: name,
        is_default: isDefault,
        street,
        house_number: houseNumber,
        bus,
        locality,
        zip_code: zipCode,
        country: JSON.parse(decodeURIComponent(atob(country)))
      } as Address,
      {
        redirectAfterSubmit
      },
      closeAfterSubmit,
      onSubmit
      )
    }
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <AddressFormFields
        form={formik}
        showSetAsDefault={showSetAsDefault}
        showDescription={showDescription}
      />
      <AddressFormAction form={formik} />
    </form>
  )
}

export default AddressForm
